import { uniq } from 'lodash/fp';

import { parseHandlebarsText } from 'dashboard/lib/utils/handlebars';
import { getConditionalRuleParts } from 'dashboard/lib/workflows';
import { BaseTrigger, BaseTriggerParams } from 'dashboard/models/trigger';
import { WorkflowConfigError, WorkflowErrorType } from 'dashboard/models/workflow';
import {
  FakeWorkflowStepType,
  WorkflowConditional,
  WorkflowElse,
  WorkflowStep,
  WorkflowStepLoopType,
  WorkflowStepOnErrorAction,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { assertNever } from 'lib/utils/types';
import { TokensToNode } from 'lib/workflows';

const extractConditionalMethodChains = (conditional: WorkflowConditional) => {
  if (conditional.rules?.or == null) {
    return [];
  }
  return conditional.rules.or.flatMap((or) => {
    if (or.and == null) {
      return [];
    }
    return or.and.flatMap((and) => {
      const parts = getConditionalRuleParts(and);
      return [parts.valueLeftConfig, parts.valueLeftConfig].flatMap((config) => {
        if (config == null) {
          return [];
        }
        return config.paths;
      });
    });
  });
};

const conditionalIsNotElse = (
  conditional: WorkflowConditional | WorkflowElse,
): conditional is WorkflowConditional => conditional.rules !== true;

// dz: this is probably okay for now as a hack. with a lack of ts reflection this is a best-effort attempt at detecting
// method chains in params in a generic way.
const extractActionMethodChains = (params: any): string[] => {
  if (params == null) {
    return [];
  }
  switch (typeof params) {
    case 'symbol':
    case 'bigint':
    case 'boolean':
    case 'function':
    case 'number':
    case 'undefined':
      return [];
    case 'object':
      if (Array.isArray(params)) {
        return params.flatMap((element) => extractActionMethodChains(element));
      } else {
        return Object.values(params).flatMap((param) => extractActionMethodChains(param));
      }

    case 'string':
      try {
        return parseHandlebarsText(params)
          .map(({ variableName, content }) => variableName ?? content)
          .filter(Boolean);
      } catch (err) {
        return [params];
      }
  }
};
const extractReferencedMethodChains = (step: WorkflowStep): string[] => {
  switch (step.type) {
    case WorkflowStepType.Action: {
      return extractActionMethodChains(step.action?.params);
    }
    case WorkflowStepType.Conditional: {
      return step.conditionals.flatMap((conditional) => {
        if (conditionalIsNotElse(conditional)) {
          return extractConditionalMethodChains(conditional);
        } else {
          return [];
        }
      });
    }
    case WorkflowStepType.Loop: {
      switch (step.settings.type) {
        case WorkflowStepLoopType.While:
          return extractConditionalMethodChains(step.settings.conditional);
        case WorkflowStepLoopType.ForEach:
          return [step.settings['array-path']];
        case WorkflowStepLoopType.Map:
          return [step.settings['array-path']];
        default:
          return assertNever(step.settings);
      }
    }
    case WorkflowStepType.Module: {
      // TODO @jkim - fill this out
      return [];
    }
    case WorkflowStepType.Placeholder:
    case WorkflowStepType.Parallel:
    case WorkflowStepType.Wait:
    case FakeWorkflowStepType.MapOutput:
      return [];
    default:
      assertNever(step);
  }
};

const stepOnErrorShouldBeContinue = (
  methodChain: string,
  tokenToStep: TokensToNode,
  memoizedResults: Record<string, boolean> = {},
): boolean => {
  if (memoizedResults[methodChain] != null) {
    return memoizedResults[methodChain];
  }
  if (methodChain === BaseTrigger || methodChain === BaseTriggerParams) {
    return false;
  }
  const step = tokenToStep[methodChain]?.step;
  if (step == null) {
    // This shouldn't happen but does in practice due to poorly configured/validated configs
    return false;
  }
  if (step.type === WorkflowStepType.Placeholder) {
    return false;
  }
  if (step['on-error'] === WorkflowStepOnErrorAction.Continue) {
    return true;
  }

  memoizedResults[methodChain] =
    uniq(extractReferencedMethodChains(step)).findIndex((methodChain) =>
      stepOnErrorShouldBeContinue(methodChain.split('.')[0], tokenToStep, memoizedResults),
    ) >= 0;
  return memoizedResults[methodChain];
};

export const validateOnError = (graph: TokensToNode): WorkflowConfigError[] => {
  const memoizedResults: Record<string, boolean> = {};
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Object.values(graph)
    .filter(
      (node) =>
        node.step.type !== WorkflowStepType.Placeholder &&
        node.step['on-error'] !== WorkflowStepOnErrorAction.Continue,
    )
    .map((node): WorkflowConfigError | null =>
      stepOnErrorShouldBeContinue(node.step.token, graph, memoizedResults)
        ? {
            token: node.step.token,
            field: 'on-error',
            message: `Step should be marked as Continue on Error since it depends on another step that is marked as Continue on Error`,
            type: WorkflowErrorType.Publish,
          }
        : null,
    )
    .filter(Boolean);
};
